/**
 * Mock ScribbleReads community data for a book — ratings, reviews, popular
 * reader tags, "currently reading" social proof, recent activity. None of
 * this exists as a real data model yet (see
 * SCRIBBLEREADS_BACKEND_IMPLEMENTATION_GUIDE.md — global per-book reviews
 * are explicitly future work), so every function here is deterministically
 * generated from the book's id, folding in whatever real seed content
 * already exists (data.js's READERS/REVIEW/SHELVES.activity,
 * seedBooks.js's friendsReading) so known/seeded books don't look thinner
 * than freshly-generated ones. Each function is written at the seam a real
 * endpoint would replace — see the TODO comment in each.
 */

import { READERS, REVIEW, SHELVES } from '@/data';
import { seededRandom, randInt, pickN, pickOne } from './deterministicMock';
import { TAG_POOL } from './tagPool';

const DAY = 24 * 60 * 60 * 1000;

const FILLER_PERSONAS = [
  { name: 'Priya', handle: 'priya.reads', tone: 'coral' },
  { name: 'Owen', handle: 'owen.turns.pages', tone: 'sage' },
  { name: 'Lena', handle: 'lena.between.chapters', tone: 'blue' },
  { name: 'Theo', handle: 'theo.reads.late', tone: 'yellow' },
  { name: 'Nadia', handle: 'nadia.bookish', tone: 'coral' },
  { name: 'Marcus', handle: 'marcus.margins', tone: 'sage' },
];

const FILLER_QUOTES = [
  'Could not put this down — read it in a single weekend.',
  'The pacing dragged a little in the middle, but the ending redeemed it completely.',
  'This book lives in my head rent-free.',
  'Beautifully written. I found myself rereading paragraphs just for the prose.',
  'Not for everyone, but I loved the slow burn.',
  'Recommended by three friends and they were all right.',
  'The kind of book you press into other people’s hands.',
  'Started slow, but by the last third I could not stop.',
  'Exactly the comfort read I needed this month.',
  'The twist genuinely got me — did not see it coming.',
];

/**
 * Overall rating + star distribution. Biased toward a real seed rating
 * (from bookshelf/seedBooks.js) when one exists, so a book you've already
 * rated doesn't show a contradictory community average.
 *
 * TODO: API call — GET /books/{bookId} → ratingSummary
 * (see SCRIBBLEREADS_BACKEND_IMPLEMENTATION_GUIDE.md §6)
 */
export function getRatingSummary(bookId, knownRating) {
  const rng = seededRandom(`rating:${bookId}`);
  const count = randInt(rng, 40, 2400);
  const average = knownRating != null
    ? Math.round(knownRating * 10) / 10
    : Math.round((3.6 + rng() * 1.2) * 10) / 10;

  const stars = [5, 4, 3, 2, 1];
  const weights = stars.map((s) => Math.max(0.03, 1 - Math.abs(s - average) / 2.2) * (0.85 + rng() * 0.3));
  const totalWeight = weights.reduce((a, b) => a + b, 0);
  const rawCounts = weights.map((w) => (w / totalWeight) * count);
  const rounded = rawCounts.map(Math.round);
  const diff = count - rounded.reduce((a, b) => a + b, 0);
  rounded[rounded.indexOf(Math.max(...rounded))] += diff;

  const distribution = stars.map((star, i) => ({ star, count: Math.max(0, rounded[i]) }));
  return { average, count, distribution };
}

/**
 * A deterministic subset of reader "vibe" tags, weighted toward this
 * book's own subjects/genres when they overlap the pool.
 *
 * TODO: API call — GET /books/{bookId}/tags (community-submitted tags)
 */
export function getPopularTags(bookId, subjects = []) {
  const rng = seededRandom(`tags:${bookId}`);
  const n = randInt(rng, 4, 7);
  return pickN(rng, TAG_POOL, n).sort(() => rng() - 0.5).slice(0, n);
}

/**
 * "N readers are currently reading this" social proof. Folds in the real
 * `friendsReading` count from bookshelf/seedBooks.js when the book has one,
 * so a book seeded with real social proof doesn't look thinner than a
 * randomly-generated one.
 *
 * TODO: API call — GET /books/{bookId}/readers?status=READING
 */
export function getCurrentlyReadingSocialProof(bookId, seedFriendsReading) {
  const rng = seededRandom(`reading:${bookId}`);
  const count = seedFriendsReading > 0 ? seedFriendsReading : randInt(rng, 0, 6);
  const sample = pickN(rng, FILLER_PERSONAS, Math.min(count, 5));
  return { count, sample };
}

/**
 * Paginated reader reviews — real ones (data.js's READERS/REVIEW, matched
 * by title) first, padded with deterministic filler so every book has a
 * plausible-looking review list.
 *
 * TODO: API call — GET /books/{bookId}/reviews (community_posts, type=review;
 * see SCRIBBLEREADS_BACKEND_IMPLEMENTATION_GUIDE.md §5)
 */
export function getReviews(bookId, bookTitle, { page = 1, pageSize = 5 } = {}) {
  const rng = seededRandom(`reviews:${bookId}`);
  const titleLower = bookTitle?.toLowerCase();

  const real = [...READERS, REVIEW]
    .filter((r) => r?.book && titleLower && r.book.toLowerCase() === titleLower)
    .map((r, i) => ({
      id: `real_${bookId}_${i}`,
      name: r.handle ?? r.name,
      handle: r.handle,
      tone: r.tone,
      rating: r.rating,
      quote: r.quote ?? r.comment,
      likes: r.likes ?? randInt(rng, 8, 220),
      createdAt: Date.now() - randInt(rng, 1, 60) * DAY,
    }));

  const fillerCount = randInt(rng, 5, 9);
  const filler = Array.from({ length: fillerCount }, (_, i) => {
    const persona = pickOne(rng, FILLER_PERSONAS);
    return {
      id: `filler_${bookId}_${i}`,
      name: persona.name,
      handle: persona.handle,
      tone: persona.tone,
      rating: randInt(rng, 3, 5),
      quote: pickOne(rng, FILLER_QUOTES),
      likes: randInt(rng, 2, 180),
      createdAt: Date.now() - randInt(rng, 1, 300) * DAY,
    };
  });

  const all = [...real, ...filler].sort((a, b) => b.likes - a.likes);
  const start = (page - 1) * pageSize;
  return {
    reviews: all.slice(start, start + pageSize),
    total: all.length,
    hasMore: start + pageSize < all.length,
  };
}

/**
 * Recent shelf activity for this book (data.js's SHELVES.activity, matched
 * by title) — "Mira finished this", "Jonah shelved this".
 *
 * TODO: API call — GET /books/{bookId}/activity
 */
export function getRecentActivity(bookId, bookTitle) {
  if (!bookTitle) return [];
  const titleLower = bookTitle.toLowerCase();
  return SHELVES.activity.filter((a) => a.what.toLowerCase() === titleLower);
}
